import defaultConfig from '@/modules/config/constants/defaultConfig';
import type { KnownConfig } from '@/modules/config/types/known-config';
import { get, merge, set } from 'lodash-es';
import { singleton } from 'tsyringe';
import type { Get, PartialDeep, Paths } from 'type-fest';

type PartialConfig = PartialDeep<KnownConfig> & Record<string, unknown>;

@singleton()
export default class Config {
  /**
   * Configuration storage
   */
  private config: PartialConfig = defaultConfig;

  /**
   * Get a configuration value
   * @param key - The key of the configuration value
   * @param defaultValue - The default value if the configuration value is not found
   * @template T - The type of the configuration value
   * @returns The configuration value or the default value if the configuration value is not found
   */
  public get<K extends Paths<KnownConfig>>(key: K): Get<KnownConfig, K>;
  public get<K extends Paths<KnownConfig>>(
    key: K,
    defaultValue: Get<KnownConfig, K>
  ): Exclude<Get<KnownConfig, K>, undefined>;
  public get<T = unknown>(key: string, defaultValue?: T): T;
  public get(key: string, defaultValue?: unknown): unknown {
    const value = get(this.config, key, defaultValue);
    return value;
  }

  /**
   * Set a configuration value
   * @param key - The key of the configuration value
   * @param value - The configuration value
   * @template T - The type of the configuration value
   */
  public set<K extends Paths<KnownConfig>>(
    key: K,
    value: Get<KnownConfig, K>
  ): void;
  public set<T = unknown>(key: string, value: T): void;
  public set(key: string, value: unknown): void {
    set(this.config, key, value);
  }

  /**
   * Load configurations from somewhere and replace the current configurations
   * @returns A promise that resolves when the configurations are loaded
   */
  protected async overwrite(): Promise<PartialConfig> {
    return this.config;
  }

  /**
   * Load configurations from somewhere and merge with the current configurations
   * @returns A promise that resolves when the configurations are loaded
   */
  protected async merge(): Promise<PartialConfig> {
    return this.config;
  }

  /**
   * Load and initialize the configurations based on `overwrite` and `merge` methods.
   * @returns A promise that resolves when the configurations are loaded and initialized
   */
  public async initialize(): Promise<PartialConfig> {
    this.config = await this.overwrite();
    const configToBeMerged = await this.merge();
    merge(this.config, configToBeMerged);
    return this.config;
  }
}
